import {
  IDisplayPairing,
  IPairingPosition,
  IWebsocketUpdatePairingEvent,
  unassignedPairingId,
  WebsocketMessageType,
} from "@vmosolutions/oc-interfaces";
import { AnyAction } from "redux";
import { ThunkAction, ThunkDispatch } from "redux-thunk";
import { IRootState } from "../../reducers/CombinedReducers";
import { CrewSchedEvent, ICrewEvent, ICrewPairingMap, newCrewPairing } from "../../reducers/CrewScheduleReducer";
import { removePairingEditor, Type } from "./CrewSchedule.Actions";

const findEvent = (id: string) => (pairing: CrewSchedEvent) => pairing?._id === id;

/**
 * use a websocket action with Thunk to update the redux store
 * for display pairings
 * @param updatedEvent -
 * @param actionType -
 */
export const webSocketUpdatePairingEvent = (
  updatedEvent: IWebsocketUpdatePairingEvent,
  actionType: WebsocketMessageType
): ThunkAction<void, IRootState, object, AnyAction> => {
  return (dispatch: ThunkDispatch<IRootState, object, AnyAction>, getState: () => IRootState): void => {
    // no update, nothing to do
    if (!updatedEvent) return;

    const { oldEvent, newEvent } = updatedEvent;
    //was a pairing deleted?
    if (oldEvent?._id && !newEvent?._id) {
      //is there an open pairing editor?
      const openEditor = !!getState().crewSchedule.pairingEditors[oldEvent._id];
      if (openEditor) {
        dispatch(removePairingEditor(oldEvent._id));
      }
    }

    const selectedCrewScheduleEvents: ICrewEvent[] = getState().crewSchedule.selectedCrewEvents;
    // Update any selected crew events that reference this pairing so they
    // stay in sync with the crew actually assigned to the updated pairing
    if (selectedCrewScheduleEvents?.length && (oldEvent || newEvent)) {
      const updatedSelectedEvents: ICrewEvent[] = [];

      selectedCrewScheduleEvents.forEach((event) => {
        const matchesOld = !!oldEvent && event.event._id === oldEvent._id;
        const matchesNew = !!newEvent && event.event._id === newEvent._id;

        if (!matchesOld && !matchesNew) {
          updatedSelectedEvents.push(event);
          return;
        }

        // If pairing has been deleted entirely, drop it from selection
        if (!newEvent) {
          return;
        }

        const crewId = event.crewId;
        const crewStillOnPairing =
          newEvent.positions?.some((pos: IPairingPosition) => {
            const mappingId = pos.crew_id ? pos.crew_id : unassignedPairingId;
            return crewId === mappingId;
          }) ?? false;

        // Only keep the selection if this crew member is still on the pairing
        // otherwise drop the stale reference
        if (crewStillOnPairing) {
          updatedSelectedEvents.push({ ...event, event: newEvent });
        }
      });
      dispatch({
        type: Type.SET_SELECTED_CREW_EVENTS,
        newEvents: updatedSelectedEvents,
        update: true,
      });
    }

    const stateMap: ICrewPairingMap = { ...getState().crewSchedule.crewPairings };
    const crewPairingMap = updatePairingMap(stateMap, oldEvent, newEvent);
    // Update Pairings used in the Crew Scheduler
    dispatch({
      type: Type.UPDATE_CREW_PAIRINGS,
      crewPairingMap,
    });

    // If there are waterfall pairings loaded into redux, update them as required
    const waterFallPairings = [...getState().crewSchedule.waterfallWindow.pairings];
    //console.log("waterFallPairings", waterFallPairings);
    //console.log(oldEvent.shortId);

    const index = waterFallPairings?.findIndex((p) => p?._id === oldEvent?._id);
    if (waterFallPairings?.length && index > -1) {
      switch (true) {
        case !!newEvent?._id: // Update entry.
          waterFallPairings[index] = newEvent;
          dispatch({
            type: Type.UPDATE_WATERFALL_PAIRINGS,
            pairings: waterFallPairings,
          });
          break;
        case !newEvent?._id: // Remove entry as pairing has been deleted
          waterFallPairings.splice(index, 1);
          dispatch({
            type: Type.UPDATE_WATERFALL_PAIRINGS,
            pairings: waterFallPairings,
          });
          return;
        default:
          return;
      }
    }
  };
};

const updatePairingMap = (
  crewPairingMap: ICrewPairingMap,
  oldEvent: IDisplayPairing | null,
  newEvent: IDisplayPairing | null
): ICrewPairingMap => {
  const lastModified = Date.now();
  const positions: IPairingPosition[] = oldEvent?.positions ? [...oldEvent.positions] : [];
  newEvent?.positions.forEach((value: IPairingPosition) => {
    if (positions.findIndex((pos: IPairingPosition) => pos.crew_id === value.crew_id) < 0) {
      positions.push(value);
    }
  });
  positions.forEach((pairingPos: IPairingPosition) => {
    // No longer return if pairing is unassigned, store in "Unassigned" map id
    // if (!pairingPos.crew_id) {
    //  return;
    // }

    const mappingId = !pairingPos.crew_id ? unassignedPairingId : pairingPos.crew_id;

    // If an entry doesn't exist in the object, add it.
    if (!crewPairingMap[mappingId]) {
      crewPairingMap[mappingId] = newCrewPairing(lastModified);
    }

    const crewPairings = crewPairingMap[mappingId];
    crewPairings.lastModified = lastModified;

    const pairings = [...crewPairings.pairings];
    const oldPairingIndex = pairings.findIndex(findEvent(oldEvent?._id));

    if (oldPairingIndex >= 0) {
      const crewInNewPairingIndex = newEvent?.positions.findIndex((newEventPairingPos: IPairingPosition) => {
        return (
          newEventPairingPos.crew_id === mappingId ||
          (!newEventPairingPos?.crew_id && mappingId === unassignedPairingId)
        );
      });
      if (newEvent && crewInNewPairingIndex >= 0) {
        pairings[oldPairingIndex] = newEvent;
      } else {
        pairings.splice(oldPairingIndex, 1);
      }
    } else {
      //you shouldn't add a pairing that this crew isn't on
      const crewIsOnPairing =
        newEvent?.positions.findIndex(
          (newEventPairingPos: IPairingPosition) =>
            newEventPairingPos.crew_id === mappingId ||
            (!newEventPairingPos?.crew_id && mappingId === unassignedPairingId)
        ) >= 0;
      if (newEvent && crewIsOnPairing) {
        pairings.push(newEvent);
      }
    }
    pairings.sort((a: IDisplayPairing, b: IDisplayPairing) => a.start?.valueOf() - b.start?.valueOf());
    crewPairings.pairings = pairings;
  });
  // console.log("crewPairingMap", crewPairingMap);
  return crewPairingMap;
};
